import React, { useState } from "react";
import { Link } from "react-router-dom";
import { FiArrowUpRight, FiCheck, FiMessageCircle } from "react-icons/fi";
import FreeQuote from "../pages/FreeQuotes/FreeQuote";

const deliverySteps = ["Discover", "Create", "Grow"];

const ServiceHero = ({
  eyebrow,
  title,
  highlight,
  description,
  features,
  image,
  imageAlt,
  imageFit = "contain",
  visualLabel,
  visualTitle,
  fitViewport = false,
}) => {
  const [showPopup, setShowPopup] = useState(false);

  const popHandler = () => {
    setShowPopup((isOpen) => !isOpen);
  };

  return (
    <>
      <section
        className={`relative isolate min-h-screen overflow-hidden bg-[#070a10] px-5 pb-16 pt-28 text-white md:px-8 md:pb-20 md:pt-32 lg:flex lg:items-center lg:px-12 ${
          fitViewport
            ? "service-hero-viewport-fit lg:min-h-0 lg:pb-5 lg:pt-[104px]"
            : "lg:pb-12 lg:pt-28"
        }`}
      >
        <div className="absolute inset-0 -z-30 bg-[radial-gradient(circle_at_9%_18%,rgba(100,60,144,0.45),transparent_28%),radial-gradient(circle_at_91%_12%,rgba(255,219,22,0.14),transparent_25%),radial-gradient(circle_at_64%_90%,rgba(35,155,230,0.16),transparent_30%)]" />
        <div className="absolute inset-0 -z-20 opacity-[0.065] [background-image:linear-gradient(rgba(255,255,255,0.45)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.45)_1px,transparent_1px)] [background-size:72px_72px] [mask-image:linear-gradient(to_bottom,black,transparent_96%)]" />
        <div className="absolute -left-28 top-36 -z-10 h-72 w-72 rounded-full border border-white/10" />
        <div className="absolute -left-12 top-52 -z-10 h-36 w-36 rounded-full border border-[#FFDB16]/20" />
        <div className="absolute right-[7%] top-28 -z-10 h-24 w-24 rotate-12 rounded-[28px] border border-white/10 bg-white/[0.025]" />

        <div
          className={`mx-auto grid w-full max-w-7xl items-center gap-14 lg:grid-cols-[0.95fr_1.05fr] ${
            fitViewport ? "lg:gap-10 xl:gap-16" : "lg:gap-16 xl:gap-24"
          }`}
        >
          <div className="relative">
            <div
              className={`inline-flex items-center gap-3 rounded-full border border-white/10 bg-white/[0.06] px-4 py-2 backdrop-blur-xl ${
                fitViewport ? "mb-3 lg:mb-3" : "mb-5"
              }`}
            >
              <span className="h-2.5 w-2.5 rounded-full bg-[#FFDB16] shadow-[0_0_20px_rgba(255,219,22,0.65)]" />
              <span className="text-sm font-bold tracking-wide text-slate-200">
                {eyebrow}
              </span>
            </div>

            <h1
              className={`max-w-3xl font-heading text-[2.75rem] font-extrabold leading-[1.03] tracking-[-0.045em] text-white sm:text-6xl ${
                fitViewport
                  ? "lg:text-[3.35rem] xl:text-[3.75rem]"
                  : "lg:text-[3.75rem] xl:text-[4.25rem]"
              }`}
            >
              {title}
              <span className="relative mt-1 block w-fit text-[#FFDB16]">
                {highlight}
                <span className="absolute -bottom-2 left-0 h-1 w-24 rounded-full bg-[#FFDB16] sm:w-32" />
              </span>
            </h1>

            <p
              className={`max-w-xl text-lg leading-8 text-slate-300 md:text-xl lg:text-lg lg:leading-7 xl:text-xl xl:leading-8 ${
                fitViewport ? "mt-5" : "mt-8"
              }`}
            >
              {description}
            </p>

            <div
              className={`grid gap-3 sm:grid-cols-2 lg:grid-cols-1 xl:grid-cols-2 ${
                fitViewport ? "mt-4" : "mt-6"
              }`}
            >
              {features.map((feature, index) => (
                <div
                  key={feature}
                  className={index === features.length - 1 ? "sm:col-span-2 lg:col-span-1 xl:col-span-2" : ""}
                >
                  <span className="flex items-center gap-3 text-sm font-semibold text-slate-200">
                    <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-[#FFDB16]/15 text-[#FFDB16]">
                      <FiCheck className="h-4 w-4" />
                    </span>
                    {feature}
                  </span>
                </div>
              ))}
            </div>

            <div
              className={`flex flex-col gap-3 sm:flex-row sm:items-center ${
                fitViewport ? "mt-6" : "mt-8"
              }`}
            >
              <button
                type="button"
                onClick={popHandler}
                className="group inline-flex min-h-[52px] items-center justify-center gap-2 rounded-full bg-[#FFDB16] px-7 py-3 font-bold text-slate-950 transition duration-300 hover:-translate-y-0.5 hover:bg-white hover:shadow-[0_16px_40px_rgba(255,219,22,0.2)]"
              >
                Discuss your project
                <FiArrowUpRight className="h-5 w-5 transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
              </button>
              <Link
                to="/contactus"
                className="inline-flex min-h-[52px] items-center justify-center gap-2 rounded-full border border-white/15 bg-white/[0.05] px-7 py-3 font-bold text-white backdrop-blur-xl transition duration-300 hover:border-white/30 hover:bg-white/[0.1]"
              >
                <FiMessageCircle className="h-4 w-4" />
                Talk to our team
              </Link>
            </div>
          </div>

          <div
            className={`relative mx-auto w-full lg:mx-0 ${
              fitViewport ? "max-w-[560px]" : "max-w-[610px]"
            }`}
          >
            <div className="absolute -inset-5 -z-10 rounded-[42px] bg-gradient-to-br from-[#FFDB16]/20 via-transparent to-[#643c90]/40 blur-2xl" />
            <div className="relative rounded-[30px] border border-white/15 bg-white/[0.07] p-3 shadow-2xl shadow-black/40 backdrop-blur-xl">
              <div
                className={`service-hero-visual relative flex h-[390px] items-center justify-center overflow-hidden rounded-[23px] bg-white p-7 sm:h-[470px] sm:p-10 ${
                  fitViewport ? "" : "lg:h-[500px]"
                }`}
              >
                <div className="absolute inset-0 opacity-50 [background-image:radial-gradient(rgba(100,60,144,0.18)_1px,transparent_1px)] [background-size:20px_20px]" />
                <img
                  src={image}
                  alt={imageAlt}
                  className={
                    imageFit === "cover"
                      ? "absolute inset-0 z-10 h-full w-full object-cover"
                      : "relative z-10 max-h-full w-full object-contain"
                  }
                />
                <div className="absolute inset-x-0 bottom-0 z-20 bg-gradient-to-t from-slate-950/90 via-slate-950/55 to-transparent px-6 pb-6 pt-20 sm:px-8 sm:pb-8">
                  <p className="text-xs font-extrabold uppercase tracking-[0.16em] text-[#FFDB16]">
                    {visualLabel}
                  </p>
                  <p className="mt-2 max-w-sm font-heading text-xl font-extrabold leading-tight text-white sm:text-2xl">
                    {visualTitle}
                  </p>
                </div>
              </div>

              <div className="mt-3 grid grid-cols-3 gap-2">
                {deliverySteps.map((step, index) => (
                  <div
                    key={step}
                    className="flex items-center justify-center gap-2 rounded-xl border border-white/10 bg-slate-950/35 px-2 py-3"
                  >
                    <span className="text-xs font-extrabold text-[#FFDB16]">0{index + 1}</span>
                    <span className="text-xs font-bold text-slate-200 sm:text-sm">{step}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="absolute -right-3 -top-3 hidden items-center gap-2 rounded-full border border-white/70 bg-white px-4 py-2 text-xs font-extrabold uppercase tracking-[0.11em] text-slate-900 shadow-xl sm:flex">
              <span className="h-2 w-2 rounded-full bg-emerald-500" />
              Taking new projects
            </div>
          </div>
        </div>
      </section>

      {showPopup ? <FreeQuote popHandler={popHandler} /> : ""}
    </>
  );
};

export default ServiceHero;
